import { useRuntimeConfig } from '#imports'

const PLACEHOLDER = '/images/team-member-placeholder-shaped.png'

const isAbsoluteHttp = (url?: string): boolean => !!url && /^https?:\/\//i.test(url)
const isCloudinary = (url?: string): boolean => !!url && /(^https?:\/\/)?res\.cloudinary\.com\//i.test(url)

// Cache for public images mapping
let publicImagesMappingCache: Record<string, string> | null = null

/**
 * Get the public images mapping. Works on both server and client.
 * On server, reads from file synchronously. On client, uses cached value from plugin.
 */
function getPublicImagesMapping(): Record<string, string> {
  // Return cached value if available
  if (publicImagesMappingCache !== null) {
    return publicImagesMappingCache
  }
  
  // On server, try to read from file synchronously
  if (import.meta.server) {
    try {
      // Use require for synchronous file access (server-side only)
      const fs = require('node:fs')
      const path = require('node:path')
      const mappingPath = path.resolve(process.cwd(), 'data', 'public-images.json')
      if (fs.existsSync(mappingPath)) {
        const raw = fs.readFileSync(mappingPath, 'utf-8')
        publicImagesMappingCache = JSON.parse(raw || '{}') as Record<string, string>
        return publicImagesMappingCache
      }
    } catch (e) {
      // Fallback if file read fails
    }
  }
  
  // On client, try to get from window global (set by plugin)
  if (import.meta.client) {
    try {
      // @ts-ignore
      const mapping = (window as any).__publicImagesMapping
      if (mapping) {
        publicImagesMappingCache = mapping
        return mapping
      }
    } catch (e) {
      // Fallback if global not available
    }
  }
  
  // Fallback to empty object
  publicImagesMappingCache = {}
  return publicImagesMappingCache
}

/**
 * Normalize an image source to respect CDN policy:
 * - Never return Airtable URLs
 * - Prefer Cloudinary public IDs (for Nuxt Image cloudinary provider)
 * - Map local public paths to Cloudinary public IDs when available
 * - Fall back to local placeholder when unknown/missing
 */
export function useImageCdn() {
  const config = useRuntimeConfig()
  const cloudBase: string = (config.public as any)?.cloudinaryBaseURL || 'https://res.cloudinary.com/ommi-shop/image/upload'

  const toCloudinaryPublicId = (candidate?: string, slug?: string): string | undefined => {
    if (!candidate) return undefined
    if (isCloudinary(candidate)) {
      // Strip base to leave public id for Nuxt Image cloudinary provider
      const idx = candidate.indexOf('/image/upload/')
      if (idx !== -1) {
        return candidate.slice(idx + '/image/upload/'.length)
      }
      // If unknown format, return as-is; Nuxt Image can still handle absolute in many cases
      return candidate
    }
    // For absolute remote URLs (e.g., Airtable), return as absolute URL.
    if (isAbsoluteHttp(candidate)) return candidate
    // Local asset path (public/...) - check if we have a Cloudinary mapping
    if (candidate.startsWith('/')) {
      const mapping = getPublicImagesMapping()
      const cloudinaryPublicId = mapping[candidate]
      if (cloudinaryPublicId) {
        // Store both the public ID and original path for extension preservation
        // Cloudinary might store public IDs without extensions, so we need to preserve
        // the extension from the original path
        return { publicId: cloudinaryPublicId, originalPath: candidate }
      }
      // No mapping found, return local path (will be served as static asset)
      return candidate
    }
    // Treat any other non-empty string as a potential Cloudinary public id (e.g. "artists/<slug>/photo")
    return candidate
  }

  const resolveForDisplay = (src?: string, slug?: string): { provider: 'cloudinary' | undefined; src: string } => {
    const resolved = toCloudinaryPublicId(src, slug)
    if (!resolved) {
      // Always use plain <img> for local placeholder
      return { provider: undefined, src: PLACEHOLDER }
    }
    
    // Handle object return (when we have both publicId and originalPath)
    if (typeof resolved === 'object' && resolved !== null && 'publicId' in resolved) {
      const { publicId, originalPath } = resolved as { publicId: string; originalPath: string }
      // Extract extension from original path
      const extMatch = originalPath.match(/\.[^.]+$/)
      const ext = extMatch ? extMatch[0] : ''
      // Always append extension - Cloudinary may have stored with double extension (.jpg.jpg)
      // when we uploaded with extension in public_id, so we need to match that
      const publicIdWithExt = ext ? `${publicId}${ext}` : publicId
      const fullUrl = toAbsoluteCdnUrl(publicIdWithExt)
      return { provider: undefined, src: fullUrl }
    }
    
    if (typeof resolved === 'string') {
      if (resolved.startsWith('/')) {
        // Local static asset → plain <img>, no IPX to avoid /_ipx/* URLs on static hosting
        return { provider: undefined, src: resolved }
      }
      // Absolute non-Cloudinary URL → render as plain <img>
      if (isAbsoluteHttp(resolved) && !isCloudinary(resolved)) {
        return { provider: undefined, src: resolved }
      }
      // Cloudinary public id - build full URL to preserve file extension
      // NuxtImg strips extensions when using format transformations, so we build the URL ourselves
      if (!resolved.startsWith('http') && !resolved.startsWith('/')) {
        // It's a Cloudinary public ID - build full URL with WebP optimization
        // This ensures the file extension is preserved in the URL
        const fullUrl = toAbsoluteCdnUrl(resolved)
        return { provider: undefined, src: fullUrl }
      }
      // Already a full Cloudinary URL
      return { provider: undefined, src: resolved }
    }
    
    // Fallback
    return { provider: undefined, src: PLACEHOLDER }
  }

  /**
   * Build an absolute Cloudinary URL with WebP optimization
   * Uses f_auto (auto format - WebP when supported) and q_auto (auto quality)
   */
  const toAbsoluteCdnUrl = (publicId?: string, options?: { width?: number; height?: number; quality?: string }) => {
    if (!publicId || publicId.startsWith('http')) {
      return publicId || ''
    }
    
    // Build Cloudinary transformations for WebP optimization
    const transformations: string[] = []
    
    if (options?.width) {
      transformations.push(`w_${options.width}`)
    }
    if (options?.height) {
      transformations.push(`h_${options.height}`)
    }
    
    // Always use auto format (WebP when supported) and auto quality
    transformations.push('f_auto') // Auto format: WebP when supported, fallback to original
    transformations.push('q_auto')  // Auto quality: optimizes for file size vs quality
    
    const transformStr = transformations.length > 0 ? `${transformations.join(',')}/` : ''
    return `${cloudBase}/${transformStr}${publicId}`
  }

  return {
    resolveForDisplay,
    placeholderSrc: PLACEHOLDER,
    toAbsoluteCdnUrl
  }
}


